import React, { useEffect, useState } from "react";

import {
    EuiPopover,
    EuiPopoverTitle,
    EuiFieldSearch,
    EuiFilterSelectItem,
    EuiFilterGroup,
    EuiFilterButton,
    EuiContextMenu,
    EuiLoadingSpinner,
    EuiSpacer,
    EuiIcon,
    EuiFlexGroup,
    EuiFlexItem,
    EuiNotificationBadge,
    EuiBadge,
    EuiButtonIcon,
} from "@elastic/eui";
import { useIntl } from "react-intl";
import _ from "lodash";
import * as S from "./style";
import Api from "../../../lib/api";
import { extractResourceAttributes, mapResourceObjectToArray } from "../../../utils/jsonApiUtils";
import DateRange from "./components/DateRange";
import { mapArrayToObject } from "../../../utils/arrayUtils";

const fetchFilterEntities = async (entityName, searchValue) => {
    const { entities } = await Api.get(
        `/${_.kebabCase(entityName)}${searchValue ? `?filter[q]=${searchValue}` : ""}`
    );
    return entities[_.camelCase(entityName)];
};

const fetchEntity = async (entityName, entityId) => {
    const { entities } = await Api.get(`/${_.kebabCase(entityName)}/${entityId}`);

    return entities[_.camelCase(entityName)];
};

const renderNotificationBadge = (notifications) =>
    !!notifications?.length && (
        <EuiNotificationBadge style={{ marginInlineStart: 10 }}>
            {notifications.length}
        </EuiNotificationBadge>
    );

const mapFieldsToContextMenuItems = (filterFields, selectedEntities, onClick, onRemove) =>
    filterFields
        .filter((field) => field.type !== "dateRange")
        .map((field) => {
            const fieldSelectedEntities = selectedEntities[field.id];

            return {
                name: (
                    <EuiFlexGroup
                        justifyContent="spaceBetween"
                        alignItems="center"
                    >
                        <EuiFlexItem style={{ flexDirection: "row" }}>
                            {field.label}
                            {renderNotificationBadge(fieldSelectedEntities)}
                        </EuiFlexItem>

                        {fieldSelectedEntities?.length && (
                            <EuiFlexItem grow={false}>
                                <EuiButtonIcon
                                    color="subdued"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemove(field.id);
                                    }}
                                    iconType="crossInACircleFilled"
                                    aria-label="Remove"
                                />
                            </EuiFlexItem>
                        )}
                    </EuiFlexGroup>
                ),
                panel: 1,
                onClick: () => {
                    onClick(field.id);
                },
            };
        });

const mapEntitiesToFilterEntities = (list, name) =>
    list.map((item) => ({
        name: name ? item[name] : _.startCase(item),
        id: item.id || item.id === 0 ? +item.id : item,
    }));

// Map from selectedFilterEntities: {driverId: [0,1,2], client: [36,30]}
// To {driverId: [{id: 0, name: '', checked: 'on'} .....], client: [{id: 30, name: '', checked: 'on'}] }
const mapFilterValuesToEntities = (selectedFilterEntities, filterFields) => {
    const selectedEntites = _.map(selectedFilterEntities, (entities, key) => {
        const filterField = filterFields.find((field) => field.id === key);
        const isConstantEntity = !!filterField?.data;

        return {
            [key]: entities.map((entityValue) => ({
                id:
                    isConstantEntity || filterField?.type === "dateRange"
                        ? entityValue
                        : +entityValue,

                name: isConstantEntity
                    ? _.startCase(entityValue)
                    : entityValue === "0"
                    ? "Unassigned"
                    : null,

                checked: "on",
            })),
        };
    });

    return mapArrayToObject(selectedEntites);
};

const markCheckedEntites = (filterSelectItems, selectedEntities) =>
    filterSelectItems.map((item) => ({
        ...item,
        checked:
            !!selectedEntities && !!selectedEntities.find((entity) => entity.id === item.id)
                ? "on"
                : null,
    }));

const mapEntitiesToParamsObject = (selectedEntities) => {
    const paramsList = _.map(selectedEntities, (entities, key) => ({
        [key]: entities.map((entity) => entity.id),
    }));

    return mapArrayToObject(paramsList);
};

function TableFilter({ filterFields, onChange, defaultValue }) {
    const [selectedField, setSelectedField] = useState({});
    const [filterEntities, setFilterEntities] = useState([]);
    const [selectedEntities, setSelectedEntities] = useState(
        defaultValue ? mapFilterValuesToEntities(defaultValue, filterFields) : {}
    );

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dateRangeField = filterFields.find((field) => field.type === "dateRange") || {};
    const intl = useIntl();

    useEffect(() => {
        getEntitiesName();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        filterEntitiesHandler();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedField]);

    const onButtonClick = () => {
        setIsPopoverOpen(!isPopoverOpen);
    };

    const closePopover = () => {
        setIsPopoverOpen(false);
    };

    const getEntitiesName = () => {
        _.map(selectedEntities, (selectedEntityValues, key) => {
            const entityField = filterFields.find((field) => field.id === key);

            selectedEntityValues.forEach((selectedEntity) => {
                if (!selectedEntity.name && entityField.entityName && selectedEntity.id !== 0) {
                    fetchEntity(entityField.entityName, selectedEntity.id).then((fetchedEntity) => {
                        selectedEntity.name =
                            extractResourceAttributes(fetchedEntity)[entityField.name];

                        setSelectedEntities({ ...selectedEntities });
                    });
                }
            });
        });
    };

    const updateFilterEntities = (response) => {
        const filterEntitiesList = selectedField.showUnassignedOption
            ? [{ id: 0, [selectedField.name]: "Unassigned" }, ...mapResourceObjectToArray(response)]
            : mapResourceObjectToArray(response);

        const selectedItems = mapEntitiesToFilterEntities(filterEntitiesList, selectedField.name);

        const markedFilterEntities = markCheckedEntites(
            selectedItems,
            selectedEntities[selectedField.id]
        );

        setFilterEntities([...markedFilterEntities]);
    };

    const filterEntitiesHandler = async () => {
        const { entityName, data } = selectedField;

        if (data) {
            setIsLoading(true);
            const markedFilterEntities = markCheckedEntites(
                mapEntitiesToFilterEntities(data),
                selectedEntities[selectedField.id]
            );

            setFilterEntities([...markedFilterEntities]);
            setIsLoading(false);
            return;
        }

        if (entityName) {
            setIsLoading(true);
            try {
                const response = await fetchFilterEntities(entityName);

                updateFilterEntities(response);
                setIsLoading(false);
            } catch (error) {
                console.log(error);
                setFilterEntities([]);
                setIsLoading(false);
            }
        }
    };

    const selectFilterFieldHandler = (id) => {
        const newSelectedField = filterFields.find((field) => field.id === id);
        setSelectedField({ ...newSelectedField });
    };

    const debounceSearch = _.debounce(async (value) => {
        setIsLoading(true);

        try {
            const response = await fetchFilterEntities(selectedField.entityName, value);
            updateFilterEntities(response);
            setIsLoading(false);
        } catch (err) {
            console.log(err);
            setIsLoading(false);
        }
    }, 500);

    const searchEntityHandler = (e) => {
        debounceSearch(e.target.value);
    };

    const panels = [
        {
            id: 0,
            title: "Filters",
            items: mapFieldsToContextMenuItems(
                filterFields,
                selectedEntities,
                selectFilterFieldHandler,
                removeSelectedEntity
            ),
        },
        {
            id: 1,
            title: (
                <>
                    {selectedField.label}
                    {renderNotificationBadge(selectedEntities[selectedField.id])}
                </>
            ),
            content: (
                <>
                    {!selectedField.data && (
                        <EuiPopoverTitle paddingSize="s">
                            <EuiFieldSearch
                                compressed
                                placeholder="Search this"
                                onChange={(e) => searchEntityHandler(e)}
                                isClearable
                                aria-label="search filters"
                            />
                        </EuiPopoverTitle>
                    )}
                    {!isLoading && (
                        <div className="euiFilterSelect__items">
                            {filterEntities.map((item, index) => (
                                <EuiFilterSelectItem
                                    checked={item.checked}
                                    key={index}
                                    onClick={() => updateItem(index)}
                                >
                                    {item.name}
                                </EuiFilterSelectItem>
                            ))}
                        </div>
                    )}
                    {isLoading && (
                        <div className="euiFilterSelect__note">
                            <div className="euiFilterSelect__noteContent">
                                <EuiLoadingSpinner size="m" />
                                <EuiSpacer size="xs" />
                                <p>Loading filters</p>
                            </div>
                        </div>
                    )}
                    {!isLoading && !filterEntities.length && (
                        <div className="euiFilterSelect__note">
                            <div className="euiFilterSelect__noteContent">
                                <EuiIcon type="minusInCircle" />
                                <EuiSpacer size="xs" />
                                <p>No filters found</p>
                            </div>
                        </div>
                    )}
                </>
            ),
        },
    ];

    function updateItem(index) {
        if (!filterEntities[index]) {
            return;
        }

        const newItems = [...filterEntities];

        switch (newItems[index].checked) {
            case "on":
                newItems[index].checked = null;
                break;

            case null:
                newItems[index].checked = "on";
                break;

            default:
                newItems[index].checked = "on";
        }

        const selectedItems = newItems.filter((item) => item.checked === "on");
        const selectedEntitiesClone = _.cloneDeep(selectedEntities);

        selectedEntitiesClone[selectedField.id] = selectedItems;

        setSelectedEntities(selectedEntitiesClone);
        onChange(mapEntitiesToParamsObject(selectedEntitiesClone));
    }

    function removeSelectedEntity(entityFieldId, entityId) {
        const selectedEntitiesClone = _.cloneDeep(selectedEntities);

        // If id not passed remove all selected entities fot this field
        selectedEntitiesClone[entityFieldId] =
            entityId || entityId === 0
                ? selectedEntitiesClone[entityFieldId].filter((entity) => entity.id !== entityId)
                : [];

        setSelectedEntities(selectedEntitiesClone);
        reRenderContextMenu();
        onChange(mapEntitiesToParamsObject(selectedEntitiesClone));
    }

    const reRenderContextMenu = () => {
        closePopover();
        setTimeout(() => {
            setIsPopoverOpen(true);
        }, 500);
    };

    const clearFilter = () => {
        const selectedEntitesClone = _.map(selectedEntities, (entityValues, key) =>
            key !== dateRangeField.id ? { [key]: [] } : { [key]: entityValues }
        );

        setSelectedEntities(mapArrayToObject(selectedEntitesClone));
        onChange(mapEntitiesToParamsObject(mapArrayToObject(selectedEntitesClone)));
        reRenderContextMenu();
    };

    const renderButton = () => {
        const selectedEntitiesClone = _.cloneDeep(selectedEntities);
        delete selectedEntitiesClone[dateRangeField?.id];

        const selectedEntitiesValue = Object.values(selectedEntitiesClone);
        const numActiveFilters = selectedEntitiesValue.reduce((sum, item) => sum + item.length, 0);
        const hasActiveFilters = numActiveFilters !== 0;

        return (
            <div className="filter-entities__wrapper">
                <EuiFilterButton
                    iconType="arrowDown"
                    onClick={onButtonClick}
                    isSelected={isPopoverOpen}
                    numFilters={filterEntities.length}
                    hasActiveFilters={hasActiveFilters}
                    numActiveFilters={numActiveFilters}
                >
                    Filter by
                </EuiFilterButton>
                {hasActiveFilters && (
                    <div className="filter-entities__result">
                        {_.map(selectedEntitiesClone, (entities, key) =>
                            entities.map((entity) => {
                                const entityFilterField = filterFields.find(
                                    (field) => field.id === key
                                );
                                const entityFilterFieldLabel = intl.formatMessage({
                                    id: entityFilterField.labelDescriptorId,
                                });

                                return (
                                    <EuiBadge
                                        key={entity.id}
                                        color="hollow"
                                        iconType="cross"
                                        iconSide="right"
                                        iconOnClickAriaLabel="remove selected entity"
                                        iconOnClick={() =>
                                            removeSelectedEntity(entityFilterField.id, entity.id)
                                        }
                                    >
                                        {`${entityFilterFieldLabel}: ${
                                            entity.name || `#${entity.id}`
                                        }`}
                                    </EuiBadge>
                                );
                            })
                        )}

                        <EuiButtonIcon
                            className="filter-entities__clear-all"
                            color="subdued"
                            onClick={clearFilter}
                            title="Clear all "
                            iconType="crossInACircleFilled"
                            aria-label="Remove"
                        />
                    </div>
                )}
            </div>
        );
    };

    const dateRangeHandler = (startDate, endDate) => {
        setSelectedEntities({
            ...selectedEntities,
            [dateRangeField.id]: [{ id: startDate }, { id: endDate }],
        });

        onChange(
            mapEntitiesToParamsObject({
                ...selectedEntities,
                [dateRangeField.id]: [{ id: startDate }, { id: endDate }],
            })
        );
    };

    return (
        <S.TableFilterWrapper>
            <EuiFlexItem>
                <EuiFilterGroup>
                    <EuiPopover
                        id="TableFilterPopover"
                        ownFocus
                        button={renderButton()}
                        isOpen={isPopoverOpen}
                        closePopover={closePopover}
                        anchorPosition="downLeft"
                        panelPaddingSize="none"
                    >
                        <EuiContextMenu
                            initialPanelId={0}
                            panels={panels}
                        />
                    </EuiPopover>
                </EuiFilterGroup>
            </EuiFlexItem>

            <EuiFlexItem>
                <DateRange
                    onChange={dateRangeHandler}
                    defaultStartDate={
                        selectedEntities[dateRangeField.id] &&
                        selectedEntities[dateRangeField.id][0].id
                    }
                    defaultEndDate={
                        selectedEntities[dateRangeField.id] &&
                        selectedEntities[dateRangeField.id][1].id
                    }
                />
            </EuiFlexItem>
        </S.TableFilterWrapper>
    );
}

export default TableFilter;
